import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

export default function PaymentSuccess() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [paymentDetails, setPaymentDetails] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      // Get checkout data to calculate correct amount
      const checkoutData = JSON.parse(localStorage.getItem('checkoutData') || '{}')
      const courses = checkoutData.items || []
      const calculatedTotal = courses.reduce((sum: number, course: any) => sum + course.price, 0)

      const paymentData = {
        transactionId: searchParams.get('razorpay_order_id') || 'TXN' + Date.now(),
        amount: searchParams.get('amount') || calculatedTotal.toString() || '0',
        paymentMethod: 'Razorpay',
        paymentId: searchParams.get('razorpay_payment_id') || '',
        orderId: searchParams.get('razorpay_order_id') || '',
        signature: searchParams.get('razorpay_signature') || '',
        status: 'success',
        timestamp: new Date().toISOString(),
        courses: courses
      }

      setPaymentDetails(paymentData)

      // Clear cart and checkout data
      localStorage.removeItem('userCart')
      localStorage.removeItem('tempCheckoutCart')
      localStorage.removeItem('checkoutData')

      setLoading(false)
    }, 2000)

    return () => clearTimeout(timer)
  }, [searchParams])

  const handleContinue = () => {
    navigate('/user/courses?refresh=true')
  }

  const handleDownloadReceipt = () => {
    if (!paymentDetails) return

    // Generate professional invoice content
    const invoiceHtml = generateInvoiceHTML(paymentDetails)

    // Create and download as HTML file (in production, this would be a PDF)
    const blob = new Blob([invoiceHtml], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Invoice-${paymentDetails.transactionId}.html`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    alert('Invoice downloaded! (In production, this would be a professional PDF)')
  }

  const generateInvoiceHTML = (payment: any) => {
    const invoiceDate = new Date(payment.timestamp).toLocaleDateString('en-IN')
    const calculatedTotal = payment.courses.reduce((sum: number, course: any) => sum + course.price, 0)
    const totalAmount = calculatedTotal.toLocaleString()

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Invoice - ${payment.transactionId}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #333; }
        .invoice-header { border-bottom: 3px solid #D97706; padding-bottom: 20px; margin-bottom: 30px; }
        .company-info { display: flex; justify-content: space-between; align-items: start; }
        .logo { font-size: 24px; font-weight: bold; color: #D97706; }
        .invoice-details { text-align: right; }
        .invoice-title { font-size: 28px; color: #1F2937; margin: 20px 0; }
        .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin: 30px 0; }
        .detail-section h3 { color: #374151; border-bottom: 1px solid #E5E7EB; padding-bottom: 5px; }
        .courses-table { width: 100%; border-collapse: collapse; margin: 30px 0; }
        .courses-table th, .courses-table td { padding: 12px; text-align: left; border-bottom: 1px solid #E5E7EB; }
        .courses-table th { background-color: #F9FAFB; font-weight: 600; }
        .total-section { text-align: right; margin-top: 30px; padding-top: 20px; border-top: 2px solid #D97706; }
        .total-amount { font-size: 24px; font-weight: bold; color: #059669; }
        .footer { margin-top: 50px; padding: 20px; background-color: #F9FAFB; border-radius: 8px; text-align: center; }
        .payment-status { display: inline-block; padding: 6px 12px; background-color: #D1FAE5; color: #059669; border-radius: 4px; font-weight: 600; }
    </style>
</head>
<body>
    <div class="invoice-header">
        <div class="company-info">
            <div>
                <div class="logo">Sudha's Academy</div>
                <p>Professional Beauty & Cosmetology Training<br>
                Email: hello@sudhasacademy.com<br>
                Phone: +91 98765-43210</p>
            </div>
            <div class="invoice-details">
                <h2>INVOICE</h2>
                <p><strong>Invoice #:</strong> ${payment.transactionId}<br>
                <strong>Date:</strong> ${invoiceDate}<br>
                <strong>Status:</strong> <span class="payment-status">PAID</span></p>
            </div>
        </div>
    </div>

    <div class="details-grid">
        <div class="detail-section">
            <h3>Payment Information</h3>
            <p><strong>Payment ID:</strong> ${payment.paymentId}<br>
            <strong>Payment Method:</strong> ${payment.paymentMethod}<br>
            <strong>Transaction Date:</strong> ${invoiceDate}</p>
        </div>
        <div class="detail-section">
            <h3>Student Information</h3>
            <p><strong>Name:</strong> ${JSON.parse(localStorage.getItem('user') || '{}').name || 'Student'}<br>
            <strong>Email:</strong> ${JSON.parse(localStorage.getItem('user') || '{}').email || 'student@example.com'}</p>
        </div>
    </div>

    <table class="courses-table">
        <thead>
            <tr>
                <th>Course</th>
                <th>Instructor</th>
                <th>Price</th>
            </tr>
        </thead>
        <tbody>
            ${payment.courses.map((course: any) => `
                <tr>
                    <td><strong>${course.title}</strong><br>
                        <small style="color: #6B7280;">Enrolled on ${new Date().toLocaleDateString('en-IN')}</small>
                    </td>
                    <td>${course.instructor || 'Sudha Beauty Expert'}</td>
                    <td>₹${course.price.toLocaleString()}</td>
                </tr>
            `).join('')}
        </tbody>
    </table>

    <div class="total-section">
        <p><strong>Subtotal:</strong> ₹${payment.courses.reduce((sum: number, course: any) => sum + course.price, 0).toLocaleString()}</p>
        <p><strong>Tax:</strong> Included</p>
        <div class="total-amount">Total Paid: ₹${payment.courses.reduce((sum: number, course: any) => sum + course.price, 0).toLocaleString()}</div>
    </div>

    <div class="footer">
        <h3>Thank you for choosing Sudha's Academy!</h3>
        <p>You now have lifetime access to your purchased courses. Start your learning journey today!</p>
        <p><strong>Support:</strong> For any queries, contact us at hello@sudhasacademy.com</p>
        <p><em>This is a computer-generated invoice and does not require a signature.</em></p>
    </div>
</body>
</html>
    `
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-rose-100 dark:from-dark-900 dark:to-dark-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-rose-200 dark:border-mauve-800 rounded-full animate-spin border-t-rose-600 dark:border-t-rose-400 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-charcoal-900 dark:text-white mb-2">
            Processing Payment...
          </h2>
          <p className="text-charcoal-600 dark:text-dark-300">
            Please wait while we confirm your transaction
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-rose-100 dark:from-dark-900 dark:to-dark-800 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Success Animation */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <svg className="w-12 h-12 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-charcoal-900 dark:text-white mb-2">
            Payment Successful! 🎉
          </h1>
          <p className="text-lg text-charcoal-600 dark:text-dark-300">
            You're now enrolled! Welcome to Sudha's Academy
          </p>
        </div>

        {/* Payment Details Card */}
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-charcoal-200 dark:border-dark-700 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-rose-600 to-rose-700 p-6 text-white">
            <h2 className="text-xl font-semibold mb-2">Payment Receipt</h2>
            <p className="text-rose-100">Transaction completed successfully</p>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-charcoal-600 dark:text-dark-300">Transaction ID</label>
                <p className="text-charcoal-900 dark:text-white font-mono bg-charcoal-50 dark:bg-dark-700 p-2 rounded text-sm">
                  {paymentDetails?.transactionId}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-charcoal-600 dark:text-dark-300">Payment ID</label>
                <p className="text-charcoal-900 dark:text-white font-mono bg-charcoal-50 dark:bg-dark-700 p-2 rounded text-sm">
                  {paymentDetails?.paymentId}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-charcoal-600 dark:text-dark-300">Amount Paid</label>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  ₹{paymentDetails?.courses.reduce((sum: number, course: any) => sum + course.price, 0).toLocaleString() || '0'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-charcoal-600 dark:text-dark-300">Payment Method</label>
                <p className="text-charcoal-900 dark:text-white font-medium">
                  {paymentDetails?.paymentMethod}
                </p>
              </div>
            </div>

            <div className="border-t border-charcoal-200 dark:border-dark-700 pt-4">
              <label className="text-sm font-medium text-charcoal-600 dark:text-dark-300 block mb-2">
                Courses Purchased
              </label>
              <div className="space-y-3">
                {paymentDetails?.courses.map((course: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-charcoal-50 dark:bg-dark-700 rounded-lg">
                    <div className="flex items-center gap-3">
                      <img
                        src={course.thumbnail}
                        alt={course.title}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div>
                        <h4 className="font-medium text-charcoal-900 dark:text-white">
                          {course.title}
                        </h4>
                        <p className="text-sm text-charcoal-600 dark:text-dark-300">
                          By {course.instructor}
                        </p>
                      </div>
                    </div>
                    <span className="font-semibold text-charcoal-900 dark:text-white">
                      ₹{course.price.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleDownloadReceipt}
            className="px-6 py-3 border border-charcoal-300 dark:border-dark-600 text-charcoal-700 dark:text-dark-300 bg-white dark:bg-dark-800 hover:bg-charcoal-50 dark:hover:bg-dark-700 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download Receipt
          </button>

          <button
            onClick={handleContinue}
            className="px-8 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            Start Learning
          </button>
        </div>

        {/* Additional Info */}
        <div className="text-center mt-8 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
            What's Next?
          </h3>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <li>• Access your courses anytime from the "My Courses" section</li>
            <li>• Download course materials and certificates</li>
            <li>• Join our community for support and discussions</li>
            <li>• Check your email for course access details</li>
          </ul>
        </div>

        {/* Support Info */}
        <div className="text-center mt-6 text-sm text-charcoal-600 dark:text-dark-300">
          <p>
            Having issues? Contact our support team at{' '}
            <a href="mailto:hello@sudhasacademy.com" className="text-rose-600 dark:text-rose-400 hover:underline">
              hello@sudhasacademy.com
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
